// Pure helpers for the detection-remarks store. Standard and encrypted
// detections live in separate tables whose ids overlap, so every store
// entry is keyed by (table, id) — never by the bare id.

/** Keep in sync with REMARKS_MAX_LENGTH in AirPlateServer/main/lib/remarks.js */
export const REMARKS_MAX_LENGTH = 2000;

export type RemarkRef = { id: number; encrypted: boolean };

/** Store key → remark text (null = explicitly no remark). */
export type DetectionRemarksMap = Record<string, string | null>;

export function remarkStoreKey(ref: RemarkRef): string {
  return `${ref.encrypted ? "e" : "s"}:${ref.id}`;
}

export function parseRemarkStoreKey(key: string): RemarkRef | null {
  const m = /^([es]):(\d+)$/.exec(key);
  if (!m) return null;
  return { encrypted: m[1] === "e", id: Number(m[2]) };
}

export function normalizeDetectionId(value: unknown): number | null {
  const id = Number(value);
  if (!Number.isSafeInteger(id) || id <= 0) return null;
  return id;
}

/** Encrypted rows are the ones carrying encrypted_sensors. */
export function isEncryptedDevice(device: unknown): boolean {
  const sensors = (device as { encrypted_sensors?: unknown[] } | null)
    ?.encrypted_sensors;
  return Array.isArray(sensors) && sensors.length > 0;
}

export function remarkRefOf(device: unknown): RemarkRef | null {
  const id = normalizeDetectionId((device as { id?: unknown } | null)?.id);
  if (id == null) return null;
  return { id, encrypted: isEncryptedDevice(device) };
}

/**
 * The ref to batch-read for a row, or null when the row needs none. MySQL list
 * rows (/scannedDevices, the encrypted lists) already carry their remark, null
 * included, so only rows without one are read: the Influx-built today/past-hour
 * rows, subscription rows. `undefined` counts as absent — JSON can't carry it,
 * so it only means a client-built row whose source had no remark field.
 */
export function remarkRefToFetch(device: unknown): RemarkRef | null {
  if ((device as { remarks?: unknown } | null)?.remarks !== undefined) return null;
  return remarkRefOf(device);
}

export function setRemark(
  prev: DetectionRemarksMap | undefined,
  ref: RemarkRef,
  remarks: string | null,
): DetectionRemarksMap {
  const base = prev ?? {};
  const key = remarkStoreKey(ref);
  if (base[key] === remarks) return base;
  return { ...base, [key]: remarks };
}

/**
 * Merges batch-read rows into the store. Without `overwrite`, entries that
 * already exist win — they were written by SSE or a local save after the
 * request went out and are newer than the response.
 */
export function mergeFetchedRemarks(
  prev: DetectionRemarksMap | undefined,
  rows: ReadonlyArray<{ id?: unknown; remarks?: unknown }>,
  encrypted: boolean,
  overwrite: boolean,
): DetectionRemarksMap {
  const base = prev ?? {};
  let next: DetectionRemarksMap | null = null;
  for (const r of rows) {
    const id = normalizeDetectionId(r?.id);
    if (id == null) continue;
    const key = remarkStoreKey({ id, encrypted });
    const remarks = typeof r?.remarks === "string" ? r.remarks : null;
    if (Object.prototype.hasOwnProperty.call(base, key)) {
      if (!overwrite || base[key] === remarks) continue;
    }
    next ??= { ...base };
    next[key] = remarks;
  }
  return next ?? base;
}
